import { useState, useEffect } from 'react';
import { Header } from './components/Header';
import { Footer } from './components/Footer';
import { MarketIndicesBanner } from './components/MarketIndicesBanner';
import { StockScreener } from './components/StockScreener';
import { StockDetailModal } from './components/StockDetailModal';
import { WatchlistView } from './components/WatchlistView';
import { PortfolioView } from './components/PortfolioView';
import { ConsultChat } from './components/ConsultChat';
import { AuthModal } from './components/AuthModal';
import { SettingsModal } from './components/SettingsModal';
import { AuthProvider } from './contexts/AuthContext';
import { Stock } from './lib/supabase';
import { marketService } from './lib/marketService';
import { TrendingUp, TrendingDown, Activity, BarChart3, Zap, Shield, Target } from 'lucide-react';

function MarketOverview({ onStockClick }: { onStockClick: (stock: Stock) => void }) {
  const [stocks, setStocks] = useState<Stock[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadStocks();
  }, []);

  const loadStocks = async () => {
    try {
      const data = await marketService.getStocks();
      setStocks(data);
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  const topGainers = [...stocks].sort((a, b) =>
    marketService.calculateChangePercent(b.price, b.prev_close) - marketService.calculateChangePercent(a.price, a.prev_close)
  ).slice(0, 5);

  const topLosers = [...stocks].sort((a, b) =>
    marketService.calculateChangePercent(a.price, a.prev_close) - marketService.calculateChangePercent(b.price, b.prev_close)
  ).slice(0, 5);

  const mostActive = [...stocks].sort((a, b) => b.volume - a.volume).slice(0, 5);

  return (
    <div className="min-h-screen">
      <section className="relative py-20 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-emerald-500/5 to-transparent"></div>
        <div className="max-w-[1600px] mx-auto px-4 lg:px-6 relative">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6">
              Trading Saham Indonesia
              <span className="gradient-text block">Lebih Cerdas</span>
            </h1>
            <p className="text-lg text-slate-400 mb-8">
              Platform analisis saham terlengkap dengan TradingView charts, prediksi buy/sell signals,
              dan konsultasi trading real-time.
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <button className="btn-primary px-8 py-3 text-lg">Mulai Trading</button>
              <button className="btn-secondary px-8 py-3 text-lg">Pelajari Fitur</button>
            </div>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-16">
            {[
              { icon: BarChart3, label: 'TradingView Charts', desc: 'Real-time charts' },
              { icon: Target, label: 'Buy/Sell Signals', desc: 'AI predictions' },
              { icon: Shield, label: 'Risk Analysis', desc: 'Portfolio protection' },
              { icon: Zap, label: 'Instant Alerts', desc: 'Price notifications' },
            ].map((feature) => {
              const Icon = feature.icon;
              return (
                <div key={feature.label} className="card p-4 text-center hover:border-emerald-500/50 transition-all">
                  <div className="w-12 h-12 bg-emerald-500/10 rounded-xl flex items-center justify-center mx-auto mb-3">
                    <Icon className="w-6 h-6 text-emerald-400" />
                  </div>
                  <h3 className="font-semibold text-white mb-1">{feature.label}</h3>
                  <p className="text-xs text-slate-500">{feature.desc}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      <section className="py-12">
        <div className="max-w-[1600px] mx-auto px-4 lg:px-6">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {[
              { title: 'Top Gainers', data: topGainers, color: 'emerald' },
              { title: 'Top Losers', data: topLosers, color: 'red' },
              { title: 'Most Active', data: mostActive, color: 'blue' },
            ].map((section) => (
              <div key={section.title} className="card p-4">
                <div className="flex items-center space-x-2 mb-4">
                  <Activity className={`w-5 h-5 text-${section.color}-400`} />
                  <h3 className="font-semibold text-white">{section.title}</h3>
                </div>
                {loading ? (
                  <div className="space-y-3">
                    {[1,2,3,4,5].map(i => <div key={i} className="h-12 bg-slate-800 rounded animate-pulse"></div>)}
                  </div>
                ) : (
                  <div className="space-y-2">
                    {section.data.map((stock) => {
                      const changePercent = marketService.calculateChangePercent(stock.price, stock.prev_close);
                      const isPositive = changePercent >= 0;
                      return (
                        <button
                          key={stock.id}
                          onClick={() => onStockClick(stock)}
                          className="w-full flex items-center justify-between p-3 bg-slate-800/50 hover:bg-slate-800 rounded-lg transition-colors"
                        >
                          <div className="flex items-center space-x-3">
                            <div className="w-8 h-8 bg-slate-700 rounded flex items-center justify-center">
                              <span className="text-xs font-bold text-emerald-400">{stock.code.slice(0,2)}</span>
                            </div>
                            <div className="text-left">
                              <div className="font-semibold text-white text-sm">{stock.code}</div>
                              <div className="text-xs text-slate-500 truncate max-w-[100px]">{stock.sector}</div>
                            </div>
                          </div>
                          <div className="text-right">
                            <div className="font-mono text-sm text-white">{marketService.formatPrice(stock.price)}</div>
                            <div className={`flex items-center justify-end text-xs font-mono ${isPositive ? 'text-emerald-400' : 'text-red-400'}`}>
                              {isPositive ? <TrendingUp className="w-3 h-3 mr-1" /> : <TrendingDown className="w-3 h-3 mr-1" />}
                              {isPositive ? '+' : ''}{changePercent.toFixed(2)}%
                            </div>
                          </div>
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}

function AnalysisPage({ onStockClick }: { onStockClick: (stock: Stock) => void }) {
  const [stocks, setStocks] = useState<Stock[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadStocks();
  }, []);

  const loadStocks = async () => {
    try {
      const data = await marketService.getStocks();
      setStocks(data);
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  const getSignal = (stock: Stock) => {
    if (stock.pe_ratio && stock.pe_ratio < 15 && stock.roe && stock.roe > 15) return 'BUY';
    if (stock.pe_ratio && stock.pe_ratio > 30) return 'SELL';
    return 'HOLD';
  };

  const getScore = (stock: Stock) => {
    let score = 50;
    if (stock.pe_ratio && stock.pe_ratio < 15) score += 15;
    if (stock.pb_ratio && stock.pb_ratio < 1.5) score += 10;
    if (stock.roe && stock.roe > 15) score += 15;
    if (stock.der && stock.der < 1) score += 10;
    return Math.min(100, score);
  };

  return (
    <div className="max-w-[1600px] mx-auto px-4 lg:px-6 py-8">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-white mb-2">Stock Analysis</h1>
        <p className="text-slate-400">Analisis fundamental dan teknikal dengan AI-powered signals</p>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-20">
          <div className="w-10 h-10 border-2 border-emerald-500 border-t-transparent rounded-full animate-spin"></div>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {stocks.map((stock) => {
            const signal = getSignal(stock);
            const score = getScore(stock);
            const changePercent = marketService.calculateChangePercent(stock.price, stock.prev_close);
            const isPositive = changePercent >= 0;

            return (
              <div key={stock.id} onClick={() => onStockClick(stock)} className="card-hover p-4 space-y-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <div className="w-12 h-12 bg-slate-800 rounded-xl flex items-center justify-center">
                      <span className="font-bold text-emerald-400">{stock.code.slice(0,2)}</span>
                    </div>
                    <div>
                      <div className="font-bold text-white">{stock.code}</div>
                      <div className="text-xs text-slate-500">{stock.sector}</div>
                    </div>
                  </div>
                  <span className={`px-3 py-1 rounded-lg text-sm font-bold ${
                    signal === 'BUY' ? 'signal-buy' : signal === 'SELL' ? 'signal-sell' : 'signal-hold'
                  }`}>{signal}</span>
                </div>

                <div className="flex items-center justify-between">
                  <div>
                    <div className="font-mono text-xl font-bold text-white">{marketService.formatPrice(stock.price)}</div>
                    <div className={`flex items-center text-sm ${isPositive ? 'text-emerald-400' : 'text-red-400'}`}>
                      {isPositive ? <TrendingUp className="w-4 h-4 mr-1" /> : <TrendingDown className="w-4 h-4 mr-1" />}
                      <span className="font-mono">{isPositive ? '+' : ''}{changePercent.toFixed(2)}%</span>
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="text-xs text-slate-500 mb-1">Health Score</div>
                    <div className={`text-2xl font-bold ${score >= 70 ? 'text-emerald-400' : score >= 50 ? 'text-amber-400' : 'text-red-400'}`}>
                      {score}
                    </div>
                  </div>
                </div>

                <div className="h-2 bg-slate-800 rounded-full overflow-hidden">
                  <div className={`h-2 rounded-full ${score >= 70 ? 'bg-emerald-500' : score >= 50 ? 'bg-amber-500' : 'bg-red-500'}`} style={{ width: `${score}%` }}></div>
                </div>

                <div className="grid grid-cols-4 gap-2 text-center text-xs">
                  {[
                    { label: 'PE', value: stock.pe_ratio?.toFixed(1) || '-' },
                    { label: 'PB', value: stock.pb_ratio?.toFixed(1) || '-' },
                    { label: 'ROE', value: stock.roe ? `${stock.roe.toFixed(0)}%` : '-' },
                    { label: 'DER', value: stock.der?.toFixed(1) || '-' },
                  ].map(item => (
                    <div key={item.label} className="bg-slate-800/50 rounded p-2">
                      <div className="text-slate-500">{item.label}</div>
                      <div className="font-mono text-white font-semibold">{item.value}</div>
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function AppContent() {
  const [currentPage, setCurrentPage] = useState('market');
  const [selectedStock, setSelectedStock] = useState<Stock | null>(null);
  const [authMode, setAuthMode] = useState<'login' | 'signup' | null>(null);
  const [portfolioRefresh, setPortfolioRefresh] = useState(0);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [telegramUser, setTelegramUser] = useState<any>(null);

  useEffect(() => {
    const savedUser = localStorage.getItem('gotradingzy_telegram_user');
    if (savedUser) {
      setTelegramUser(JSON.parse(savedUser));
    }
  }, []);

  const handleStockClick = (stock: Stock) => setSelectedStock(stock);
  const handleAddToPortfolio = () => setPortfolioRefresh(prev => prev + 1);
  const handleLogout = () => {
    localStorage.removeItem('gotradingzy_telegram_user');
    setTelegramUser(null);
  };

  const renderPage = () => {
    switch (currentPage) {
      case 'market': return <MarketOverview onStockClick={handleStockClick} />;
      case 'screener': return (
        <div className="max-w-[1600px] mx-auto px-4 lg:px-6 py-8">
          <div className="mb-8">
            <h1 className="text-3xl font-bold text-white mb-2">Stock Screener</h1>
            <p className="text-slate-400">Filter dan temukan saham terbaik untuk portfolio Anda</p>
          </div>
          <StockScreener onStockClick={handleStockClick} />
        </div>
      );
      case 'analysis': return <AnalysisPage onStockClick={handleStockClick} />;
      case 'portfolio': return (
        <div className="max-w-[1600px] mx-auto px-4 lg:px-6 py-8">
          <div className="mb-8">
            <h1 className="text-3xl font-bold text-white mb-2">Portfolio</h1>
            <p className="text-slate-400">Kelola dan pantau performa investasi Anda</p>
          </div>
          <PortfolioView refresh={portfolioRefresh} />
        </div>
      );
      case 'watchlist': return (
        <div className="max-w-[1600px] mx-auto px-4 lg:px-6 py-8">
          <div className="mb-8">
            <h1 className="text-3xl font-bold text-white mb-2">Watchlist</h1>
            <p className="text-slate-400">Pantau saham favorit Anda</p>
          </div>
          <WatchlistView onStockClick={handleStockClick} />
        </div>
      );
      case 'consult': return (
        <div className="max-w-4xl mx-auto px-4 lg:px-6 py-8">
          <div className="mb-8">
            <h1 className="text-3xl font-bold text-white mb-2">Trading Consultant</h1>
            <p className="text-slate-400">Konsultasi trading dengan AI assistant</p>
          </div>
          <ConsultChat />
        </div>
      );
      default: return <MarketOverview onStockClick={handleStockClick} />;
    }
  };

  return (
    <div className="min-h-screen bg-[#0a0e17] flex flex-col">
      <Header
        onAuthClick={setAuthMode}
        currentPage={currentPage}
        onNavigate={setCurrentPage}
        onSettingsClick={() => setSettingsOpen(true)}
        telegramUser={telegramUser}
      />
      <MarketIndicesBanner />
      <main className="flex-1">
        {renderPage()}
      </main>
      <Footer />
      <StockDetailModal stock={selectedStock} onClose={() => setSelectedStock(null)} onAddToPortfolio={handleAddToPortfolio} />
      <AuthModal mode={authMode} onClose={() => setAuthMode(null)} />
      <SettingsModal isOpen={settingsOpen} onClose={() => setSettingsOpen(false)} onLogout={handleLogout} />
    </div>
  );
}

function App() {
  return (
    <AuthProvider>
      <AppContent />
    </AuthProvider>
  );
}

export default App;
